import React, { useCallback, useEffect, useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { useHistory, useLocation, useParams } from "react-router-dom";
import AiTextImprovement from "../../../../components/ai/AiTextImprovement";
import PageHeader from "../../../../app/layout/PageHeader";
import { api } from "../../../../api/apiClient";
import { buildDocumentFileName, DocumentActions } from "../../../../shared/document-export";
import ServiceInstanceImagesCard from "../../manage-services-attendance/ServiceInstanceImagesCard";
import VoiceTextarea from "./VoiceTextarea";
import {
  generateIncidentsPdf,
  generateRetrospectivePdf,
  generateSessionPlanPdf,
} from "./sessionReportDocumentService";
import { notify } from "../../../components-ui/toast";
import useAiFeatureAvailability from "../../../../hooks/useAiFeatureAvailability";
import "../../manage-services-attendance/RegistrationPage.css";
import "./SessionReportPage.css";

interface RouteParams { serviceId: string; }
interface ServiceOption { serviceId: number; name: string; }
interface InstanceOption { serviceInstanceId: number; startDateTime: string; status: string; }
interface SessionPlan {
  serviceInstanceId: number; objectives?: string | null; activities?: string | null;
  resources?: string | null; timingNotes?: string | null; responsibilities?: string | null;
  notes?: string | null; lastUpdatedDate?: string | null;
}
interface Reporting {
  serviceInstanceId: number; facebookMessage?: string | null;
  communityPortalText?: string | null; lastUpdatedDate?: string | null;
}
interface Retrospective {
  serviceInstanceId: number; outcomes?: string | null; whatWorkedWell?: string | null;
  challenges?: string | null; lessonsLearned?: string | null; followUpActions?: string | null;
  summary?: string | null; keyActionPoints?: string | null;
  lastUpdatedDate?: string | null;
}
interface Incident {
  serviceInstanceIncidentReportId: number; serviceInstanceId: number; incidentDateTime: string;
  peopleInvolved?: string | null; description: string; immediateAction?: string | null;
  severity: string; safeguardingEscalated: boolean; followUpRequired: boolean;
  followUpActions?: string | null; status: string; createdDate?: string; lastUpdatedDate?: string | null;
}
type ReportTab = "reporting" | "plan" | "retrospective" | "incident";

const newIncident = (): Incident => ({
  serviceInstanceIncidentReportId: 0, serviceInstanceId: 0,
  incidentDateTime: new Date().toISOString().slice(0, 16), description: "",
  severity: "Low", safeguardingEscalated: false, followUpRequired: false, status: "Open",
});
const localDateTime = (value: string) => value ? new Date(value).toISOString().slice(0, 16) : "";

export default function SessionReportPage() {
  const history = useHistory();
  const location = useLocation();
  const { serviceId: routeServiceId } = useParams<RouteParams>();
  const aiEnabled = useAiFeatureAvailability();
  const requestedInstanceId = Number(
    new URLSearchParams(location.search).get("serviceInstanceId"),
  );
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [instances, setInstances] = useState<InstanceOption[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(Number(routeServiceId) || null);
  const [selectedInstanceId, setSelectedInstanceId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<ReportTab>("plan");
  const [reporting, setReporting] = useState<Reporting | null>(null);
  const [plan, setPlan] = useState<SessionPlan | null>(null);
  const [retrospective, setRetrospective] = useState<Retrospective | null>(null);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [incident, setIncident] = useState<Incident>(newIncident());
  const [saving, setSaving] = useState(false);

  useEffect(() => { api<ServiceOption[]>("/api/services").then(setServices); }, []);
  useEffect(() => {
    setSelectedInstanceId(null); setReporting(null); setPlan(null); setRetrospective(null); setIncidents([]);
    if (!selectedServiceId) { setInstances([]); return; }
    api<InstanceOption[]>(`/api/serviceinstances/search-by-service?serviceId=${selectedServiceId}`).then((result) => {
      setInstances(result);
      if (requestedInstanceId && result.some((item) => item.serviceInstanceId === requestedInstanceId)) {
        setSelectedInstanceId(requestedInstanceId);
      }
    });
  }, [requestedInstanceId, selectedServiceId]);
  useEffect(() => {
    if (!selectedInstanceId) return;
    Promise.all([
      api<Reporting>(`/api/serviceinstances/${selectedInstanceId}/reporting`),
      api<SessionPlan>(`/api/serviceinstances/${selectedInstanceId}/plan`),
      api<Retrospective>(`/api/serviceinstances/${selectedInstanceId}/retrospective`),
      api<Incident[]>(`/api/serviceinstances/${selectedInstanceId}/incidents`),
    ]).then(([loadedReporting, loadedPlan, loadedRetrospective, loadedIncidents]) => {
      setReporting(loadedReporting); setPlan(loadedPlan); setRetrospective(loadedRetrospective); setIncidents(loadedIncidents);
      setIncident({ ...newIncident(), serviceInstanceId: selectedInstanceId });
    }).catch(() => notify.error("Failed to load the session report."));
  }, [selectedInstanceId]);

  const saveReporting = async () => {
    if (!selectedInstanceId || !reporting) return; setSaving(true);
    try { setReporting(await api(`/api/serviceinstances/${selectedInstanceId}/reporting`, { method: "PUT", body: reporting })); notify.success("Marketing content saved."); }
    finally { setSaving(false); }
  };

  const savePlan = async () => {
    if (!selectedInstanceId || !plan) return; setSaving(true);
    try { setPlan(await api(`/api/serviceinstances/${selectedInstanceId}/plan`, { method: "PUT", body: plan })); notify.success("Session plan saved."); }
    finally { setSaving(false); }
  };
  const saveRetrospective = async () => {
    if (!selectedInstanceId || !retrospective) return; setSaving(true);
    try { setRetrospective(await api(`/api/serviceinstances/${selectedInstanceId}/retrospective`, { method: "PUT", body: retrospective })); notify.success("Retrospective saved."); }
    finally { setSaving(false); }
  };
  const saveIncident = async () => {
    if (!selectedInstanceId || !incident.description.trim()) { notify.error("Enter an incident description."); return; }
    setSaving(true);
    try {
      const saved = await api<Incident>(`/api/serviceinstances/${selectedInstanceId}/incidents`, {
        method: "POST", body: { ...incident, serviceInstanceId: selectedInstanceId, incidentDateTime: new Date(incident.incidentDateTime).toISOString() },
      });
      setIncidents((current) => [saved, ...current.filter((item) => item.serviceInstanceIncidentReportId !== saved.serviceInstanceIncidentReportId)]);
      setIncident({ ...newIncident(), serviceInstanceId: selectedInstanceId }); notify.success("Incident report saved.");
    } finally { setSaving(false); }
  };
  const editIncident = (item: Incident) => setIncident({ ...item, incidentDateTime: localDateTime(item.incidentDateTime) });
  const deleteIncident = async (id: number) => {
    if (!selectedInstanceId || !window.confirm("Delete this incident report?")) return;
    await api(`/api/serviceinstances/${selectedInstanceId}/incidents/${id}`, { method: "DELETE" });
    setIncidents((current) => current.filter((item) => item.serviceInstanceIncidentReportId !== id));
    if (incident.serviceInstanceIncidentReportId === id) setIncident({ ...newIncident(), serviceInstanceId: selectedInstanceId });
    notify.success("Incident report deleted.");
  };

  const tabs: Array<{ key: ReportTab; label: string; icon: React.ReactNode }> = [
    { key: "plan", label: "Plan", icon: <AssignmentIcon /> }, { key: "retrospective", label: "Review", icon: <AssessmentIcon /> },
    { key: "reporting", label: "Marketing", icon: <PhotoLibraryIcon /> }, { key: "incident", label: "Incidents", icon: <ReportProblemIcon /> },
  ];
  const field = (label: string, value: string | null | undefined, onChange: (value: string) => void, rows = 4) => (
    <div className="form-group"><label className="form-label">{label}</label><VoiceTextarea rows={rows} value={value ?? ""} onChange={onChange} /></div>
  );
  const selectedService = useMemo(
    () => services.find((service) => service.serviceId === selectedServiceId),
    [selectedServiceId, services],
  );
  const selectedInstance = useMemo(
    () => instances.find((item) => item.serviceInstanceId === selectedInstanceId),
    [instances, selectedInstanceId],
  );
  const reportContext = useMemo(() => ({
    serviceName: selectedService?.name ?? "Service",
    instanceStartDate: selectedInstance?.startDateTime ?? new Date().toISOString(),
    instanceStatus: selectedInstance?.status,
  }), [selectedInstance, selectedService?.name]);
  const planFileName = useMemo(() => buildDocumentFileName({
    documentType: "session-plan",
    serviceName: selectedService?.name,
    date: selectedInstance?.startDateTime,
  }), [selectedInstance?.startDateTime, selectedService?.name]);
  const retrospectiveFileName = useMemo(() => buildDocumentFileName({
    documentType: "retrospective",
    serviceName: selectedService?.name,
    date: selectedInstance?.startDateTime,
  }), [selectedInstance?.startDateTime, selectedService?.name]);
  const incidentsFileName = useMemo(() => buildDocumentFileName({
    documentType: "incidents",
    serviceName: selectedService?.name,
    date: selectedInstance?.startDateTime,
  }), [selectedInstance?.startDateTime, selectedService?.name]);
  const generatePlanDocument = useCallback(
    () => generateSessionPlanPdf(reportContext, plan ?? {}),
    [plan, reportContext],
  );
  const generateRetrospectiveDocument = useCallback(
    () => generateRetrospectivePdf(reportContext, retrospective ?? {}),
    [reportContext, retrospective],
  );
  const generateIncidentsDocument = useCallback(
    () => generateIncidentsPdf(reportContext, incidents),
    [incidents, reportContext],
  );

  return <div className="page-container session-report-page">
    <PageHeader title="Session planning" subtitle="Plan, review and report on a service session." actions={<button type="button" className="icon-button-muted-solid" onClick={() => history.goBack()} title="Back" aria-label="Back"><ArrowBackIcon fontSize="small" /></button>} />
    <details className="card session-report-picker" open={!selectedServiceId || !selectedInstanceId}>
      <summary>
        <span className="session-report-picker__icon"><EventOutlinedIcon fontSize="small" /></span>
        <span className="session-report-picker__selection">
          <small>Selected session</small>
          <strong>{selectedService?.name ?? "Choose a service and session"}</strong>
          {selectedInstance && <span>{new Date(selectedInstance.startDateTime).toLocaleString()} · {selectedInstance.status}</span>}
        </span>
        <span className="session-report-picker__action">{selectedInstance ? "Change" : "Choose"}</span>
      </summary>
      <div className="session-report-picker__fields">
        <div className="form-group registration-session-filter-field"><label className="form-label" htmlFor="report-service">Service</label><select id="report-service" className="form-input registration-session-select" value={selectedServiceId ?? ""} onChange={(e) => setSelectedServiceId(e.target.value ? Number(e.target.value) : null)}><option value="">Select a service</option>{services.map((service) => <option key={service.serviceId} value={service.serviceId}>{service.name}</option>)}</select></div>
        <div className="form-group registration-session-filter-field"><label className="form-label" htmlFor="report-instance">Date and time</label><select id="report-instance" className="form-input registration-session-select" value={selectedInstanceId ?? ""} disabled={!selectedServiceId} onChange={(e) => setSelectedInstanceId(e.target.value ? Number(e.target.value) : null)}><option value="">{selectedServiceId ? "Select a date and time" : "Select a service first"}</option>{instances.map((item) => <option key={item.serviceInstanceId} value={item.serviceInstanceId}>{new Date(item.startDateTime).toLocaleString()} — {item.status}</option>)}</select></div>
      </div>
    </details>
    {selectedServiceId && selectedInstanceId && <>
      <div className="session-report-navigation">
        <div className="registration-tabs session-report-tabs">{tabs.map((tab) => <button key={tab.key} type="button" className={`registration-tab ${activeTab === tab.key ? "active" : ""}`} onClick={() => setActiveTab(tab.key)}>{tab.icon}<span>{tab.label}</span></button>)}</div>
        {activeTab !== "reporting" && (
          <div className="session-report-document-toolbar">
            {activeTab === "plan" && plan && <DocumentActions documentType="session-plan" title={`${selectedService?.name ?? "Service"} session plan`} fileName={planFileName} generateDocument={generatePlanDocument} shareText="Session plan from FrontLine." compact security={{ confirmBeforeSharing: true }} />}
            {activeTab === "retrospective" && retrospective && <DocumentActions documentType="session-retrospective" title={`${selectedService?.name ?? "Service"} retrospective`} fileName={retrospectiveFileName} generateDocument={generateRetrospectiveDocument} shareText="Session retrospective from FrontLine." compact security={{ confirmBeforeSharing: true }} />}
            {activeTab === "incident" && <DocumentActions documentType="session-incidents" title={`${selectedService?.name ?? "Service"} incidents`} fileName={incidentsFileName} generateDocument={generateIncidentsDocument} shareText="Confidential incident report from FrontLine." disabled={incidents.length === 0} compact security={{ confidential: true, confirmBeforeSharing: true }} />}
          </div>
        )}
      </div>
      {activeTab === "reporting" && reporting && <div className="session-report-reporting-layout">
        <section className="card session-report-editor">
          <div className="session-report-compact-heading">
            <div className="card-title">Marketing content</div>
            <div className="field-hint">Prepare text to share alongside this session's images.</div>
          </div>
          <div className="session-report-field-grid">
            {field("Facebook message", reporting.facebookMessage, (v) => setReporting({ ...reporting, facebookMessage: v }), 6)}
            {field("Community portal text", reporting.communityPortalText, (v) => setReporting({ ...reporting, communityPortalText: v }), 6)}
          </div>
          <div className="button-row button-row-left"><button type="button" className="action-button" onClick={saveReporting} disabled={saving}>{saving ? "Savingâ€¦" : "Save marketing"}</button>{reporting.lastUpdatedDate && <span className="field-hint">Last saved {new Date(reporting.lastUpdatedDate).toLocaleString()}</span>}</div>
        </section>
        <ServiceInstanceImagesCard serviceInstanceId={selectedInstanceId} />
      </div>}
      {activeTab === "plan" && plan && (
        <section className="card session-report-editor session-plan-editor">
          <div className="session-report-section-heading session-ai-header">
            <div>
              <div className="card-title">Session plan</div>
              <div className="field-hint">Set the purpose, activities and practical details for this session.</div>
            </div>
            {aiEnabled && <details className="session-report-ai session-report-ai--compact-trigger">
              <summary aria-label="Plan with AI" title="Plan with AI">
                <span className="session-report-ai__icon"><AutoFixHighIcon fontSize="small" /></span>
                <span className="session-report-ai__copy">
                  <strong>Plan with AI</strong>
                  <small>Improve your draft or generate practical ideas and games.</small>
                </span>
              </summary>
              <div className="session-report-ai__content">
                <AiTextImprovement
                  documentType="SessionPlan"
                  fields={[{ name: "objectives", label: "Objectives", value: plan.objectives }, { name: "activities", label: "Activities", value: plan.activities }, { name: "resources", label: "Resources", value: plan.resources }, { name: "timingNotes", label: "Timing notes", value: plan.timingNotes }, { name: "responsibilities", label: "Responsibilities", value: plan.responsibilities }, { name: "notes", label: "Additional notes", value: plan.notes }]}
                  actions={[
                    { value: "SuggestContent", label: "Generate a complete session plan" },
                    { value: "SuggestActivities", label: "Suggest games and activities" },
                    { value: "Improve", label: "Improve wording" },
                    { value: "Shorten", label: "Make more concise" },
                    { value: "Structure", label: "Improve structure" },
                    { value: "Professional", label: "Use a professional tone" },
                  ]}
                  onApply={(fields) => setPlan({ ...plan, ...fields })}
                />
              </div>
            </details>}
          </div>

          <div className="session-plan-sections">
            <section className="session-plan-section">
              <div className="session-plan-content-grid">
                {field("Objectives", plan.objectives, (v) => setPlan({ ...plan, objectives: v }), 4)}
                {field("Activities", plan.activities, (v) => setPlan({ ...plan, activities: v }), 7)}
              </div>
            </section>

            <section className="session-plan-section">
              <div className="session-plan-section__heading">
                <h3>Practical details</h3>
                <p>Capture what is needed, the timings and who is responsible.</p>
              </div>
              <div className="session-plan-details-grid">
                {field("Resources", plan.resources, (v) => setPlan({ ...plan, resources: v }), 4)}
                {field("Timing notes", plan.timingNotes, (v) => setPlan({ ...plan, timingNotes: v }), 4)}
                {field("Responsibilities", plan.responsibilities, (v) => setPlan({ ...plan, responsibilities: v }), 4)}
              </div>
              <div className="session-plan-notes">
                {field("Additional notes", plan.notes, (v) => setPlan({ ...plan, notes: v }), 3)}
              </div>
            </section>
          </div>

          <div className="session-plan-footer">
            <button type="button" className="action-button" onClick={savePlan} disabled={saving}>{saving ? "Saving…" : "Save plan"}</button>
            {plan.lastUpdatedDate && <span className="field-hint">Last saved {new Date(plan.lastUpdatedDate).toLocaleString()}</span>}
          </div>
        </section>
      )}
      {activeTab === "retrospective" && retrospective && (
        <section className="card session-report-editor">
          <div className="session-report-section-heading session-ai-header">
            <div>
              <div className="card-title">Session Retrospective</div>
              <div className="field-hint">Record what happened, then use AI to tidy your notes or draft a summary and action points.</div>
            </div>
            {aiEnabled && <details className="session-report-ai session-report-ai--compact-trigger">
              <summary aria-label="Review with AI" title="Review with AI">
                <span className="session-report-ai__icon"><AutoFixHighIcon fontSize="small" /></span>
                <span className="session-report-ai__copy">
                  <strong>Review with AI</strong>
                  <small>Tidy your notes or create a summary and action points.</small>
                </span>
              </summary>
              <div className="session-report-ai__content">
                <AiTextImprovement
                  documentType="Retrospective"
                  actions={[
                    { value: "Improve", label: "Tidy wording" },
                    { value: "Structure", label: "Improve structure and formatting" },
                    { value: "Shorten", label: "Make more concise" },
                    { value: "Summarize", label: "Generate summary and action points" },
                  ]}
                  fields={[
                    { name: "outcomes", label: "Outcomes", value: retrospective.outcomes },
                    { name: "whatWorkedWell", label: "What worked well", value: retrospective.whatWorkedWell },
                    { name: "challenges", label: "Challenges", value: retrospective.challenges },
                    { name: "lessonsLearned", label: "Lessons learned", value: retrospective.lessonsLearned },
                    { name: "followUpActions", label: "Follow-up actions", value: retrospective.followUpActions },
                    { name: "summary", label: "AI-generated summary", value: retrospective.summary },
                    { name: "keyActionPoints", label: "AI-generated key action points", value: retrospective.keyActionPoints },
                  ]}
                  allowFocus={false}
                  guidance="AI suggestions are drafts based only on the review text you entered. Check every fact and action before accepting and saving."
                  onApply={(fields) => setRetrospective({ ...retrospective, ...fields })}
                />
              </div>
            </details>}
          </div>
          <div className="session-report-field-grid">
            {field("Outcomes", retrospective.outcomes, (v) => setRetrospective({ ...retrospective, outcomes: v }))}
            {field("What worked well", retrospective.whatWorkedWell, (v) => setRetrospective({ ...retrospective, whatWorkedWell: v }))}
            {field("Challenges", retrospective.challenges, (v) => setRetrospective({ ...retrospective, challenges: v }))}
            {field("Lessons learned", retrospective.lessonsLearned, (v) => setRetrospective({ ...retrospective, lessonsLearned: v }))}
            <div className="session-report-grid-wide">
              {field("Follow-up actions", retrospective.followUpActions, (v) => setRetrospective({ ...retrospective, followUpActions: v }))}
            </div>
            <div className="session-retrospective-ai-output session-report-grid-wide">
              <div className="session-retrospective-ai-output__header">
                <span className="session-retrospective-ai-output__badge">
                  <AutoAwesomeIcon fontSize="small" />
                  AI-generated draft
                </span>
                <span>Generated from the review above. Check and edit these suggestions before saving.</span>
              </div>
              <div className="session-retrospective-ai-output__fields">
                {field("AI-generated summary", retrospective.summary, (v) => setRetrospective({ ...retrospective, summary: v }), 3)}
                {field("AI-generated key action points", retrospective.keyActionPoints, (v) => setRetrospective({ ...retrospective, keyActionPoints: v }), 3)}
              </div>
            </div>
          </div>
          <div className="button-row button-row-left">
            <button type="button" className="action-button" onClick={saveRetrospective} disabled={saving}>
              {saving ? "Saving…" : "Save retrospective"}
            </button>
          </div>
        </section>
      )}
      {activeTab === "incident" && <section className="session-report-incident-layout"><div className="card session-report-editor"><div className="session-report-compact-heading"><div className="card-title">{incident.serviceInstanceIncidentReportId ? "Edit incident" : "Record an incident"}</div><div className="field-hint">Only include information necessary for recording and following up the incident.</div></div><div className="session-report-field-grid"><div className="form-group"><label className="form-label">Date and time</label><input type="datetime-local" className="form-input" value={incident.incidentDateTime} onChange={(e) => setIncident({ ...incident, incidentDateTime: e.target.value })} /></div><div className="form-group"><label className="form-label">Severity</label><select className="form-input" value={incident.severity} onChange={(e) => setIncident({ ...incident, severity: e.target.value })}>{["Low", "Medium", "High", "Critical"].map((v) => <option key={v}>{v}</option>)}</select></div>{field("People involved", incident.peopleInvolved, (v) => setIncident({ ...incident, peopleInvolved: v }), 3)}{field("Description *", incident.description, (v) => setIncident({ ...incident, description: v }))}{field("Immediate action taken", incident.immediateAction, (v) => setIncident({ ...incident, immediateAction: v }))}<div className="form-group"><label className="form-label">Status</label><select className="form-input" value={incident.status} onChange={(e) => setIncident({ ...incident, status: e.target.value })}>{["Open", "In progress", "Closed"].map((v) => <option key={v}>{v}</option>)}</select><label className="session-report-check"><input type="checkbox" checked={incident.safeguardingEscalated} onChange={(e) => setIncident({ ...incident, safeguardingEscalated: e.target.checked })} /> Safeguarding escalated</label><label className="session-report-check"><input type="checkbox" checked={incident.followUpRequired} onChange={(e) => setIncident({ ...incident, followUpRequired: e.target.checked })} /> Follow-up required</label></div>{incident.followUpRequired && <div className="session-report-grid-wide">{field("Follow-up actions", incident.followUpActions, (v) => setIncident({ ...incident, followUpActions: v }))}</div>}</div><AiTextImprovement documentType="Incident" fields={[{ name: "peopleInvolved", label: "People involved", value: incident.peopleInvolved }, { name: "description", label: "Description", value: incident.description }, { name: "immediateAction", label: "Immediate action taken", value: incident.immediateAction }]} guidance="AI can improve the wording but may make mistakes. Check that the record remains factual and accurate before saving. AI does not make safeguarding decisions." onApply={(fields) => setIncident({ ...incident, ...fields })} /><div className="button-row button-row-left"><button type="button" className="action-button" onClick={saveIncident} disabled={saving}>{saving ? "Saving…" : "Save incident"}</button>{incident.serviceInstanceIncidentReportId > 0 && <button type="button" className="secondary-button" onClick={() => setIncident({ ...newIncident(), serviceInstanceId: selectedInstanceId })}>Cancel edit</button>}</div></div>
        <div className="card session-incident-list"><div className="session-report-section-heading"><div><div className="card-title">Recorded incidents</div><div className="field-hint">Download, print or securely share the saved incident records.</div></div></div>{incidents.length === 0 ? <div className="field-hint">No incidents have been recorded for this session.</div> : incidents.map((item) => <div className="session-incident-item" key={item.serviceInstanceIncidentReportId}><div><strong>{new Date(item.incidentDateTime).toLocaleString()}</strong><span className="field-hint">{item.severity} · {item.status}</span><p>{item.description}</p></div><div className="session-incident-actions"><button type="button" className="icon-button-muted-solid" title="Edit incident" onClick={() => editIncident(item)}><EditOutlinedIcon fontSize="small" /></button><button type="button" className="icon-button-muted-solid" title="Delete incident" onClick={() => deleteIncident(item.serviceInstanceIncidentReportId)}><DeleteOutlineIcon fontSize="small" /></button></div></div>)}</div></section>}
    </>}
  </div>;
}
